/**
 * Event-type-aware formatting for surfacing the most useful at-a-glance
 * details on event cards and rows.
 *
 * The previous implementation used a single generic extractor that walked
 * `event.data.object` looking for any field named `name`, `status`, etc.
 * That approach is type-blind: a payment event with a customer's `name`
 * field would surface the name instead of the more useful amount, and
 * a customer event would never surface its email even when present.
 *
 * This module replaces that with a small registry of type-specific
 * formatters, keyed by the event-type prefix (`customer.*`, `payment.*`,
 * etc.). Each formatter returns a structured `FormattedEvent` so the UI
 * can apply consistent visual emphasis (errors in red, etc.) and stack
 * primary/secondary fields cleanly.
 *
 * Adding support for a new event family is a matter of writing one
 * formatter and registering it — the generic fallback covers anything
 * unhandled.
 */
import type { PlatformEvent } from '../types'
import { buildChangedFields, titleCase } from './changedFields'

// ── Public types ──────────────────────────────────────────────────────────────

export type EmphasisTone = 'error' | 'warning' | 'success' | 'info' | 'neutral'

export interface FormattedBadge {
  /** Short label shown inside the badge (e.g. "card", "email"). */
  label: string
  /** Optional tone — drives color/border. Defaults to neutral. */
  tone?: EmphasisTone
  /** Optional title attribute for hover detail. */
  title?: string
}

export interface FormattedEvent {
  /** Most identifying detail (name, automation, amount). Empty if not extractable. */
  primary?: string
  /** Status / outcome / supporting detail. */
  secondary?: string
  /** Visual tone for the secondary value. */
  emphasis?: EmphasisTone
  /**
   * A human-friendly customer label resolved from the payload (name, email,
   * phone). The raw `customer_id` is always preserved on the card for copy
   * purposes — this is shown alongside it, not as a replacement.
   */
  customerLabel?: string
  /** Lightweight inline badges (channel, method, etc.). */
  badges?: FormattedBadge[]
}

// ── Internal helpers ──────────────────────────────────────────────────────────

type EventPayload = Record<string, unknown>
type EventFormatter = (event: PlatformEvent, data: EventPayload) => FormattedEvent | null

/**
 * Track which event ids we've already logged the "no data.object" fallback
 * for, so we don't spam the console when React re-renders the same rows
 * during a scroll or filter change.
 */
const _formatFallbackLogged = new Set<string>()
function logFormatFallback(event: PlatformEvent): void {
  const id = event?.id
  if (!id) return
  if (_formatFallbackLogged.has(id)) return
  _formatFallbackLogged.add(id)
  // Cap the set size so a long session doesn't grow unbounded.
  if (_formatFallbackLogged.size > 500) {
    const first = _formatFallbackLogged.values().next().value
    if (first !== undefined) _formatFallbackLogged.delete(first)
  }
  console.warn('[EventDebug] formatter fallback (no data.object):', {
    id,
    type: event?.type,
    hasData: event?.data != null,
    dataKeys: event?.data ? Object.keys(event.data) : [],
    dataIsObject: event?.data && typeof event.data === 'object',
    customer_id: event?.customer_id,
    resource_name: event?.resource_name,
    source: event?.source,
  })
}

/** Read a string field from a payload, trimmed and non-empty, or null. */
function readString(data: EventPayload, key: string): string | null {
  const value = data[key]
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/** Read the first non-empty string field from a list of keys. */
function readFirstString(data: EventPayload, keys: string[]): string | null {
  for (const key of keys) {
    const value = readString(data, key)
    if (value) return value
  }
  return null
}

/** Read a number field, accepting numeric strings as a fallback. */
function readNumber(data: EventPayload, key: string): number | null {
  const value = data[key]
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = parseFloat(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

/** Truncate a string with an ellipsis. */
function truncate(value: string, maxLen: number): string {
  return value.length > maxLen ? `${value.slice(0, maxLen)}...` : value
}

/** Format a money amount that may be in major units (49.00) or cents (4900). */
function formatAmount(amount: number, currency: string | null): string {
  // Heuristic: amounts >= 1000 with no decimal probably represent cents.
  // This matches Stripe-style APIs which use minor units. We keep it loose
  // because we don't have schema-level certainty here.
  const isCents = amount >= 1000 && Number.isInteger(amount)
  const major = isCents ? amount / 100 : amount
  const cur = (currency || 'USD').toUpperCase()
  return `${cur} ${major.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

/**
 * Determine the visual emphasis to apply to a status/state string.
 * Recognises a small dictionary of known terminal states; anything else
 * defaults to neutral.
 */
function statusEmphasis(status: string): EmphasisTone {
  const lower = status.toLowerCase()
  if (
    lower.includes('fail') ||
    lower.includes('error') ||
    lower.includes('reject') ||
    lower === 'cancelled' ||
    lower === 'canceled' ||
    lower === 'refunded' ||
    lower === 'disputed' ||
    lower === 'declined'
  ) {
    return 'error'
  }
  if (
    lower === 'pending' ||
    lower === 'queued' ||
    lower === 'processing' ||
    lower === 'retrying' ||
    lower === 'timeout' ||
    lower === 'timed_out' ||
    lower === 'warning'
  ) {
    return 'warning'
  }
  if (
    lower === 'succeeded' ||
    lower === 'success' ||
    lower === 'completed' ||
    lower === 'paid' ||
    lower === 'active' ||
    lower === 'delivered' ||
    lower === 'sent' ||
    lower === 'received'
  ) {
    return 'success'
  }
  return 'neutral'
}

/** Read a recipient as a string (handles both string and { email, name } shapes). */
function readRecipient(data: EventPayload): string | null {
  const recipient = data.recipient
  if (typeof recipient === 'string') return recipient.trim() || null
  if (recipient && typeof recipient === 'object') {
    const r = recipient as EventPayload
    return readFirstString(r, ['email', 'phone', 'name'])
  }
  return null
}

/** Read an error message from an `error` field that may be a string or object. */
function readErrorMessage(data: EventPayload): string | null {
  const error = data.error
  if (typeof error === 'string') return error.trim() || null
  if (error && typeof error === 'object') {
    const e = error as EventPayload
    return readFirstString(e, ['message', 'description', 'reason'])
  }
  return null
}

/** Extract a customer-facing label from a payload (name, email, phone). */
function readCustomerLabel(data: EventPayload): string | null {
  // Prefer name when both are available — names are easier to recognise than
  // emails at a glance, and emails are still copyable from the detail modal.
  const name = readFirstString(data, ['name', 'full_name', 'display_name'])
  if (name) return truncate(name, 40)

  // Try first + last composition before falling back to other identifiers.
  const first = readString(data, 'first_name')
  const last = readString(data, 'last_name')
  if (first && last) return truncate(`${first} ${last}`, 40)
  if (first) return truncate(first, 40)

  const email = readFirstString(data, ['email', 'email_address'])
  if (email) return truncate(email, 40)

  const phone = readFirstString(data, ['phone', 'phone_number'])
  if (phone) return truncate(phone, 40)

  return null
}

// ── Formatters ────────────────────────────────────────────────────────────────

/**
 * customer.* — sign-ins, sign-ups, profile updates.
 *
 * Primary: the customer-facing identifier (name / email / phone) so an
 * operator can recognise *who* the event is about without copying the ID.
 *
 * Secondary: for `customer.updated`, summarise which fields changed using
 * `previous_attributes`; for state changes, surface the status verb.
 */
const customerFormatter: EventFormatter = (event, data) => {
  const customerLabel = readCustomerLabel(data)
  const result: FormattedEvent = {
    primary: customerLabel || undefined,
    customerLabel: customerLabel || undefined,
  }

  // For `customer.updated`, surface what actually changed — much more
  // informative than the generic "Updated" label alone. The diff itself is
  // derived by the shared `buildChangedFields` helper (task 4050b) so the
  // card summary and the detail views stay in sync.
  const previousAttrs = event.data?.previous_attributes
  if (event.type === 'customer.updated' && previousAttrs && typeof previousAttrs === 'object') {
    const changedLabels = buildChangedFields(event).map((change) => change.label)
    if (changedLabels.length === 1) {
      result.secondary = `${changedLabels[0]} changed`
    } else if (changedLabels.length > 1) {
      const list = changedLabels.slice(0, 2).join(', ')
      const extra = changedLabels.length > 2 ? ` +${changedLabels.length - 2}` : ''
      result.secondary = `${list}${extra} changed`
    }
    result.emphasis = 'info'
    return result
  }

  // For sign-ins/outs and other state transitions, lean on the status field.
  const status = readFirstString(data, ['status', 'state'])
  if (status) {
    result.secondary = titleCase(status)
    result.emphasis = statusEmphasis(status)
  }

  // If the event type itself encodes the action (e.g. `customer.signed_in`),
  // surface that as a fallback secondary — it's redundant with the type label
  // shown above, but only kicks in when we have nothing else.
  if (!result.secondary) {
    const verb = event.type.split('.')[1]
    if (verb && verb !== 'updated') {
      result.secondary = titleCase(verb)
    }
  }

  return result
}

/**
 * payment.* / purchase.* / refund.* / charge.* — financial events.
 *
 * Primary: amount + currency (the financial impact at a glance).
 * Secondary: status, with strong tone for failures/refunds/disputes.
 * Badges: payment method when available.
 * customerLabel: pulled out independently if present.
 */
const paymentFormatter: EventFormatter = (_event, data) => {
  const amount =
    readNumber(data, 'amount') ||
    readNumber(data, 'amount_total') ||
    readNumber(data, 'total') ||
    readNumber(data, 'value')
  const currency = readFirstString(data, ['currency', 'currency_code'])

  const result: FormattedEvent = {}

  if (amount !== null) {
    result.primary = formatAmount(amount, currency)
  } else {
    // No amount — fall back to a name/description if available so we still
    // have something to anchor the event.
    result.primary =
      readFirstString(data, ['description', 'name', 'product_name']) || undefined
  }

  const status = readFirstString(data, ['status', 'state'])
  const errorMessage = readErrorMessage(data)
  if (errorMessage) {
    result.secondary = `Error: ${truncate(errorMessage, 50)}`
    result.emphasis = 'error'
  } else if (status) {
    result.secondary = titleCase(status)
    result.emphasis = statusEmphasis(status)
  }

  // Payment method as a badge — operators care whether a failed payment was
  // card vs ach vs apple-pay because failure modes differ by method.
  const method = readFirstString(data, ['payment_method', 'method', 'method_type'])
  if (method) {
    result.badges = [{ label: titleCase(method), tone: 'neutral' }]
  }

  // Customer name/email if the payment object carries it.
  const customerLabel = readCustomerLabel(data)
  if (customerLabel) result.customerLabel = customerLabel

  return result
}

/**
 * agent.* — agent action events (action_taken, completed, failed, etc.).
 *
 * Primary: the agent name or the action verb taken.
 * Secondary: outcome (succeeded/failed/timed_out) with appropriate tone.
 * Badges: tools used or model name when present.
 */
const agentFormatter: EventFormatter = (_event, data) => {
  const agentName = readFirstString(data, [
    'agent_name',
    'agent',
    'name',
    'automation_name',
  ])
  const action = readFirstString(data, ['action', 'tool_name', 'tool', 'verb'])

  const result: FormattedEvent = {}

  if (agentName && action) {
    result.primary = `${agentName} → ${titleCase(action)}`
  } else if (agentName) {
    result.primary = agentName
  } else if (action) {
    result.primary = titleCase(action)
  }

  const errorMessage = readErrorMessage(data)
  const outcome = readFirstString(data, ['outcome', 'status', 'state'])
  if (errorMessage) {
    result.secondary = `Error: ${truncate(errorMessage, 50)}`
    result.emphasis = 'error'
  } else if (outcome) {
    result.secondary = titleCase(outcome)
    result.emphasis = statusEmphasis(outcome)
  }

  // Model name / tools — small badges for context.
  const badges: FormattedBadge[] = []
  const model = readFirstString(data, ['model'])
  if (model) badges.push({ label: model, tone: 'neutral', title: `Model: ${model}` })
  if (badges.length > 0) result.badges = badges

  // Some agent events include the customer they acted on behalf of.
  const customerLabel = readCustomerLabel(data)
  if (customerLabel) result.customerLabel = customerLabel

  return result
}

/**
 * automation.* / workflow.* — automation step / run events.
 *
 * Primary: automation name + step type when available.
 * Secondary: outcome with tone, or error.
 */
const automationFormatter: EventFormatter = (_event, data) => {
  const automationName = readFirstString(data, ['automation_name', 'workflow_name', 'name'])
  const stepType = readFirstString(data, ['step_type', 'step'])
  const trigger = readFirstString(data, ['trigger', 'trigger_type'])

  const result: FormattedEvent = {}

  if (automationName && stepType) {
    result.primary = `${automationName} · ${titleCase(stepType)}`
  } else if (automationName) {
    result.primary = automationName
  } else if (stepType) {
    result.primary = titleCase(stepType)
  }

  const errorMessage = readErrorMessage(data)
  const status = readFirstString(data, ['status', 'state', 'outcome'])
  if (errorMessage) {
    result.secondary = `Error: ${truncate(errorMessage, 50)}`
    result.emphasis = 'error'
  } else if (status) {
    result.secondary = titleCase(status)
    result.emphasis = statusEmphasis(status)
  }

  if (trigger) {
    result.badges = [{ label: titleCase(trigger), tone: 'neutral', title: `Trigger: ${trigger}` }]
  }

  return result
}

/**
 * session.* — session lifecycle events.
 *
 * Primary: a session-level identifier when present (resource_name often
 * gives this for free; data.title or data.subject are also common).
 * Secondary: end reason for `session.ended`, otherwise status.
 */
const sessionFormatter: EventFormatter = (event, data) => {
  const result: FormattedEvent = {}

  const title = readFirstString(data, ['title', 'subject', 'name'])
  if (title) {
    result.primary = truncate(title, 60)
  }

  // session.ended often carries an `end_reason` that's much more informative
  // than just "ended" — surface it prominently.
  const endReason = readFirstString(data, ['end_reason', 'reason', 'close_reason'])
  if (event.type.endsWith('.ended') && endReason) {
    result.secondary = titleCase(endReason)
    result.emphasis = statusEmphasis(endReason)
  } else {
    const status = readFirstString(data, ['status', 'state'])
    if (status) {
      result.secondary = titleCase(status)
      result.emphasis = statusEmphasis(status)
    }
  }

  // Turn / message counts give a sense of session weight — useful when
  // skimming a list of completed sessions.
  const turnCount =
    readNumber(data, 'turn_count') ||
    readNumber(data, 'message_count') ||
    readNumber(data, 'turns')
  if (turnCount !== null && turnCount > 0) {
    const label = turnCount === 1 ? '1 turn' : `${turnCount.toLocaleString()} turns`
    result.badges = [{ label, tone: 'neutral' }]
  }

  const customerLabel = readCustomerLabel(data)
  if (customerLabel) result.customerLabel = customerLabel

  return result
}

/**
 * communication.* / notification.* / message.* / email.* / sms.* — outbound
 * messaging events.
 *
 * Primary: subject/template/message-type.
 * Secondary: recipient — "To: foo@bar.com".
 * Badges: channel (email / sms / push).
 */
const communicationFormatter: EventFormatter = (event, data) => {
  const result: FormattedEvent = {}

  const subject = readFirstString(data, ['subject', 'template_name', 'template', 'title'])
  const messageType = readFirstString(data, ['message_type', 'type'])
  if (subject) {
    result.primary = truncate(subject, 60)
  } else if (messageType) {
    result.primary = titleCase(messageType)
  }

  const errorMessage = readErrorMessage(data)
  if (errorMessage) {
    result.secondary = `Error: ${truncate(errorMessage, 50)}`
    result.emphasis = 'error'
  } else {
    const recipient = readRecipient(data) || readString(data, 'to')
    if (recipient) {
      result.secondary = `To: ${truncate(recipient, 40)}`
      result.emphasis = 'info'
    } else {
      const status = readFirstString(data, ['status', 'state'])
      if (status) {
        result.secondary = titleCase(status)
        result.emphasis = statusEmphasis(status)
      }
    }
  }

  // Channel as a small badge (email / sms / push).
  const channel = readFirstString(data, ['channel', 'medium'])
  if (channel) {
    result.badges = [{ label: titleCase(channel), tone: 'neutral' }]
  } else {
    // Infer channel from the event type prefix as a fallback so push/email
    // are still distinguished even when the payload doesn't tag itself.
    const prefix = event.type.split('.')[0]
    if (['email', 'sms', 'push', 'notification'].includes(prefix)) {
      result.badges = [{ label: titleCase(prefix), tone: 'neutral' }]
    }
  }

  return result
}

/**
 * Generic fallback formatter — the original behaviour from EventCard. Used
 * for any event type without a dedicated handler, or when the dedicated
 * handler returns nothing extractable.
 */
const genericFormatter: EventFormatter = (_event, data) => {
  const result: FormattedEvent = {}

  // Identifying name / title / step type.
  const primary =
    readFirstString(data, [
      'name',
      'title',
      'subject',
      'automation_name',
      'summary',
      'description',
    ]) ||
    (readString(data, 'step_type') ? titleCase(data.step_type as string) : null) ||
    (readString(data, 'message_type') ? titleCase(data.message_type as string) : null)
  if (primary) {
    result.primary = truncate(primary, 60)
  }

  // Outcome / error / status.
  const errorMessage = readErrorMessage(data)
  if (errorMessage) {
    result.secondary = `Error: ${truncate(errorMessage, 50)}`
    result.emphasis = 'error'
  } else {
    const status = readFirstString(data, ['status', 'state', 'outcome'])
    if (status) {
      result.secondary = titleCase(status)
      result.emphasis = statusEmphasis(status)
    }
  }

  // Action target as a final fallback (recipient / amount / count).
  if (!result.secondary) {
    const recipient = readRecipient(data) || readString(data, 'to')
    if (recipient) {
      result.secondary = `To: ${truncate(recipient, 40)}`
      result.emphasis = 'info'
    } else {
      const amount = readNumber(data, 'amount')
      const currency = readFirstString(data, ['currency'])
      if (amount !== null) {
        result.secondary = formatAmount(amount, currency)
      } else {
        const count =
          readNumber(data, 'count') ||
          readNumber(data, 'item_count') ||
          readNumber(data, 'quantity')
        if (count !== null && count > 0) {
          result.secondary = `${count.toLocaleString()} ${count === 1 ? 'item' : 'items'}`
        }
      }
    }
  }

  return result
}

// ── Registry ──────────────────────────────────────────────────────────────────

/**
 * Map of event-type prefix to formatter. Order matters only insofar as the
 * matching loop walks the entries in declaration order; the first match
 * that returns a non-empty result wins.
 */
const FORMATTER_REGISTRY: Array<{ prefix: string; formatter: EventFormatter }> = [
  { prefix: 'customer', formatter: customerFormatter },
  // applife.* events (e.g. applife.signed_up) carry customer-identity payloads
  // identical to customer.* events. Registered here so they surface name/email
  // correctly in the timeline (task 3520h).
  { prefix: 'applife', formatter: customerFormatter },
  { prefix: 'payment', formatter: paymentFormatter },
  { prefix: 'purchase', formatter: paymentFormatter },
  { prefix: 'charge', formatter: paymentFormatter },
  { prefix: 'refund', formatter: paymentFormatter },
  { prefix: 'invoice', formatter: paymentFormatter },
  { prefix: 'agent', formatter: agentFormatter },
  { prefix: 'automation', formatter: automationFormatter },
  { prefix: 'workflow', formatter: automationFormatter },
  { prefix: 'session', formatter: sessionFormatter },
  { prefix: 'communication', formatter: communicationFormatter },
  { prefix: 'notification', formatter: communicationFormatter },
  { prefix: 'message', formatter: communicationFormatter },
  { prefix: 'email', formatter: communicationFormatter },
  { prefix: 'sms', formatter: communicationFormatter },
  { prefix: 'push', formatter: communicationFormatter },
]

/** True when a FormattedEvent has at least one user-visible field. */
function hasContent(formatted: FormattedEvent | null): formatted is FormattedEvent {
  if (!formatted) return false
  return Boolean(
    formatted.primary ||
      formatted.secondary ||
      formatted.customerLabel ||
      (formatted.badges && formatted.badges.length > 0),
  )
}

/**
 * Resolve a `FormattedEvent` for the given platform event. Picks the
 * type-specific formatter when one matches the event's prefix, then falls
 * back to the generic extractor for any remaining empty fields. The
 * returned object is always a fresh shallow copy.
 */
export function formatEvent(event: PlatformEvent): FormattedEvent {
  const data = (event.data?.object as EventPayload | undefined) ?? null

  // No payload at all — fall back to resource_name + id-prefix so identical
  // typed events are still distinguishable in the list.
  if (!data) {
    // Log the fallback path so we can correlate "events showing as #abc12345"
    // in the UI with the events that came back without `data.object`.
    // Throttled to once per event id per session via a module-level Set so we
    // don't spam the console while React re-renders the same row.
    logFormatFallback(event)
    const fallback: FormattedEvent = {}
    if (event.resource_name) {
      fallback.primary = event.resource_name
    }
    if (event.id) {
      const idPrefix = event.id.startsWith('evt_')
        ? event.id.slice(4, 12)
        : event.id.slice(0, 8)
      fallback.secondary = `#${idPrefix}`
    }
    return fallback
  }

  const prefix = event.type.split('.')[0]
  const entry = FORMATTER_REGISTRY.find((e) => e.prefix === prefix)

  let result: FormattedEvent = {}
  if (entry) {
    const typed = entry.formatter(event, data)
    if (hasContent(typed)) result = { ...typed }
  }

  // Backfill anything the type-specific formatter didn't supply with the
  // generic extractor — this keeps coverage broad for events whose payload
  // shape doesn't match the canonical fields the typed formatter expects.
  if (!result.primary || !result.secondary) {
    const generic = genericFormatter(event, data)
    if (generic) {
      if (!result.primary && generic.primary) result.primary = generic.primary
      if (!result.secondary && generic.secondary) {
        result.secondary = generic.secondary
        if (!result.emphasis) result.emphasis = generic.emphasis
      }
    }
  }

  // Final resource_name fallback for the primary slot — better to show the
  // resource than to leave the row labelless.
  if (!result.primary && event.resource_name) {
    result.primary = event.resource_name
  }

  return result
}

/**
 * Convenience helper for places that previously consumed
 * `getEventSummary(event)` and just want the primary/secondary strings as
 * a flat array.
 */
export function getEventSummaryParts(event: PlatformEvent): string[] {
  const formatted = formatEvent(event)
  const parts: string[] = []
  if (formatted.primary) parts.push(formatted.primary)
  if (formatted.secondary) parts.push(formatted.secondary)
  return parts
}
